import { create } from 'zustand';

import { clearTokens, readTokens, writeTokens } from '@/shared/lib/http';

import { authApi } from './auth.api';
import type { CurrentUser, LoginRequest, LoginResponse } from './auth.types';

/**
 * Store global de autenticação (Zustand).
 *
 * Single source of truth pro estado de auth no app. Sobre acoplamento:
 * componentes consomem via hooks (`useAuthUser`, `useIsAuthenticated`)
 * em vez do store direto — facilita refactor.
 *
 * Lifecycle:
 *   1. App start → `bootstrap()` lê tokens do localStorage, chama /me
 *      pra hidratar `user`. Se falhar, limpa silenciosamente — guard
 *      de rota redireciona depois.
 *   2. Login → `login(creds)` chama /auth/login, persiste tokens,
 *      hidrata user.
 *   3. Logout → `logout()` revoga refresh no server (best-effort) +
 *      limpa estado local.
 */
interface AuthState {
  readonly user: CurrentUser | null;
  readonly isAuthenticated: boolean;
  /** True depois do bootstrap inicial — bloqueia render de guards antes. */
  readonly ready: boolean;

  bootstrap: () => Promise<void>;
  login: (req: LoginRequest) => Promise<LoginResponse>;
  /** Hidrata `user` via /me (best-effort + timeout). Não altera isAuthenticated. */
  loadProfile: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: CurrentUser | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  ready: false,

  setUser: (user) => set({ user, isAuthenticated: user !== null }),

  bootstrap: async () => {
    const tokens = readTokens();
    if (!tokens) {
      set({ ready: true });
      return;
    }
    // Se o refresh já venceu, nem tenta /me — pede login.
    if (new Date(tokens.refreshTokenExpiresAt).getTime() < Date.now()) {
      clearTokens();
      set({ ready: true });
      return;
    }
    try {
      const user = await authApi.me();
      set({ user, isAuthenticated: true, ready: true });
    } catch {
      clearTokens();
      set({ ready: true });
    }
  },

  login: async (req) => {
    const tokens = await authApi.login(req);
    writeTokens(tokens);
    // Token válido = sessão autenticada. Seta JÁ (síncrono) pra o guard
    // de rota `/app` passar na navegação seguinte. Antes o isAuthenticated
    // só virava true depois de um /me em background não-awaited → o guard
    // via false, redirecionava pro /login e o loading travava.
    set({ isAuthenticated: true });
    return tokens;
  },

  // Best-effort: hidrata o perfil pro topbar. NUNCA trava o login — se
  // /me demorar (>5s) ou falhar, segue com user null (carrega depois).
  // Não mexe em isAuthenticated: a sessão já é válida pelos tokens.
  loadProfile: async () => {
    try {
      const user = await Promise.race<CurrentUser>([
        authApi.me(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('me-timeout')), 5000),
        ),
      ]);
      set({ user });
    } catch {
      // mantém autenticado; user fica null (fallback no topbar)
    }
  },

  logout: async () => {
    const tokens = readTokens();
    if (tokens) {
      // Best-effort — front limpa estado independente de o backend aceitar.
      authApi.logout(tokens.refreshToken).catch(() => undefined);
    }
    clearTokens();
    set({ user: null, isAuthenticated: false });
  },
}));

/**
 * Selectors estáveis (referência igual entre renders se valor não muda) —
 * evita re-render de subscriber só por re-criar objeto.
 */
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectAuthReady = (state: AuthState) => state.ready;
export const selectAuthUser = (state: AuthState) => state.user;
